import { DialogTitle, TextField, DialogActions, Button, IconButton, MenuItem, Select } from "@mui/material";
import {Price, TFrequency} from "../models/Price";
import { Refresh } from "@mui/icons-material";
import { useRef, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDialog } from "../libs/dialog/DialogProvider";
import PortUtils from "../portlibs/PortUtils";
import {ValidSectorOptions} from "../portlibs/SectorUtils"
import dayjs from "dayjs";
import EvalText from "../components/EvalText";
import CheckButton from "../libs/button/CheckButton";
import Dropdown from "../libs/dropdown/Dropdown";
import SectorDist, { type TSectorDistRefProps } from "./Config/SectorDist";
import { ESectors } from "../shared/Consts";
import PortAccount from "../models/PortAccount";

export interface IPriceProps {
    price:Price;
}

interface IPriceConfigError {
    name?:boolean;
    price?:boolean;
    payout?:boolean;
    yield?:boolean;
    other?:string;
}

export default function PriceConfig(props:IPriceProps){
    const {closeDialog} = useDialog();
    const {price} = props;
    const [name,setName] = useState<string>(price.name||"")
    const [currentPrice,setPrice] = useState<string>(price.price.toString())
    const [payout,setPayout] = useState<string>(price.payout.toString())
    const [yld,setYield] = useState<string>(price.yield.toFixed(3))
    const [busy,setBusy] = useState<boolean>(false);
    const [frequency,setFreq] = useState<string>(price.frequency.toString())
    const [date,setDate] = useState<dayjs.Dayjs>(dayjs(price.date))
    const [error,setError] = useState<IPriceConfigError>({})
    const [ex,setEx] = useState<string>(price.ex.toString())
    const [variableDividend,setVariableDividend] = useState<boolean>(price.variableDividend)
    const [currency,setCurrency] = useState<number>(price.currency)
    const [sector,setSector] = useState<ESectors>(price.sector || ESectors.Other)
    const compositions = useRef<TSectorDistRefProps>()

    const autoComplete = PortUtils.disableAutoComplete;
    const freq = ["m","q","s","a"].map(freq => {
        return <MenuItem value={freq} key={freq}>{Price.frequencyLabel(freq as any)}</MenuItem>
    })
    const cur = [0,1].map(cur => {
        return <MenuItem value={cur} key={cur}>{cur ? "USD" : "CAD"}</MenuItem>
    })
    
    const onRefresh = () => {}
    const onSave = async () => {
        const p = price.clone()

        if (!name) return setError({name:true})
        p.name = name
        p.variableDividend = variableDividend
        p.date = date.toDate();
        p.frequency = frequency as TFrequency
        p.currency = currency
        p.sector = sector
        p.sectors = compositions.current?.composition
        p.ex = parseFloat(ex)
        p.price = parseFloat(currentPrice);
        if (!(p.price>0)) return setError({price:true})

        p.payout = parseFloat(payout);
        if (isNaN(p.payout)) return setError({payout:true})

        p.yield = parseFloat(yld);
        if (isNaN(p.yield) || p.yield<0) return setError({yield:true})
        
        setBusy(true)
        try {
            await PortAccount.i.updatePrice(p)
            closeDialog();
        }
        catch(e){setBusy(false)}
    }

    const onPayout = (text:string) => {
        if (!text) return setYield("0")
        const val = parseFloat(text)
        const price = parseFloat(currentPrice)
        setYield((val/price*100).toFixed(3))
    }
    const onYield = (text:string) => {
        if (!text) return

        const val = parseFloat(text);
        const price = parseFloat(currentPrice);
        setPayout( (price*val/100).toFixed(3) )
    }
    const onEx = (val:string) => {
        setEx((parseInt(val)||0).toString())
    }
    return <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DialogTitle>
            <div className="flex middle between">
                <div>{`${price.name} (${price.id})`}</div>
                <IconButton onClick={onRefresh}><Refresh/></IconButton>
            </div>
        </DialogTitle>
        <div className="content">
            <TextField fullWidth error={error.name} className="col4" label="Name" variant="outlined" value={name} onChange={(e)=>setName(e.target.value)} {...autoComplete}/>
            <Dropdown options={ValidSectorOptions} value={sector} onChange={s => s!==null && setSector(s)} label="Sector"/>
            <div className="flex gap-8">
                <EvalText fullWidth label="Price" variant="outlined" value={currentPrice} onChange={(e)=>setPrice(e.target.value)} onEval={v=>setPrice(v)}/>
                <Select className="select" value={currency} onChange={(e)=>setCurrency(parseInt(e.target.value as string))}>{cur}</Select>
            </div>
            <EvalText fullWidth label="Payout" variant="outlined" value={payout} onChange={e=>setPayout(e.target.value)} onEval={v=>onPayout(v)}/>
            <EvalText fullWidth label="Yield" variant="outlined" value={yld} onChange={e=>setYield(e.target.value)} onEval={v=>onYield(v)}/>
            <CheckButton key="updateCash" checked={variableDividend} onChange={()=>setVariableDividend(!variableDividend)} label="Variable Dividend"/>
            <EvalText fullWidth label="Ex (days)" variant="outlined" value={ex} onChange={e=>onEx(e.target.value)} onEval={v=>onEx(v)}/>
            <DatePicker value={date} onChange={(e:any)=>setDate(e)} label="Dividend"/>
            <Select className="select" value={frequency} onChange={(e)=>setFreq(e.target.value)}>{freq}</Select>
            { price.sectors || sector==ESectors.Mixed ? <SectorDist composition={price.sectors} ref={compositions}/> : null }
        </div>
        <DialogActions>
            <Button onClick={onSave} disabled={busy}>Save</Button>
        </DialogActions>
    </LocalizationProvider>
}

export function usePriceConfig(){
    const {openDialog} = useDialog()
    return (price:Price) => {
        openDialog({id:"config", children:<PriceConfig price={price}/>})
    }
}
